<template>
  <div
    v-if="bcDocumentStore.loading"
    class="Content__Document !pb-0  mx-auto overflow-hidden"
  >
    <document-loading />
  </div>
  <div
    v-else-if="isEveryElementTypeLocked"
    class="Content__Document DocumentLocked__Wrapper xs:!pb-0  mx-auto overflow-hidden"
  >
    <document-locked/>
  </div>
  <aw-drop
    v-else
    :list="childrenFiltered"
    :can-drop-item="canDropItem"
    :create-placeholder="createPlaceholder"
    transition-name="document-elements"
    list-item-key="key"
    @drop-item="drop"
    class="break-words"
    v-aw-driver-tour="{
      name: 'BrandCloud:Document:default_New', 
      active: !asideModalDisplayed,
      settings: {
        edit: bcDocumentStore.edit,
        eshop: editProducts,
      }
    }"
  >
    <template #header>
      <aw-paddinger-top/>
      <div
        v-if="isEveryElementLocked"
        class="Content__Document !bg-bc-blue mx-auto flex flex-row justify-center space-x-2 !p-4"
      >
        <aw-icon icon="lock" class="w-5 h-5 text-white " />
        <span class="text-white text-sm">{{ $t('Document is locked') }}</span>
      </div>

      <!--Search state -->
      <document-search
        v-if="bcDocumentStore.search && !bcDocumentStore.edit"
        :found-results="foundSearchResults"
      />

      <!--Approval stat? -->
      <document-approval
        v-if="bcDocumentStore.document.approval"
        :approval="bcDocumentStore.document.approval"
        :document="bcDocumentStore.document"
        :edit="bcDocumentStore.edit"
      />
    </template>
    <template #default="{ item, index, isDropActive }">
      <document-page
        :key="item.key"
        :element="item"
        :parent="bcDocumentStore.document"
        :page="item"
        :index="index"
        :is-dragging-page="isDropActive"
        v-bc-get-box-model="{name: item.id ? 'documentPage' + item.id : null}"
        @edit-products="toggleEditProducts"
      />
    </template>
    <template #footer="{ isDropActive }">
      <!-- Approve or disapprove?  -->
      <document-approval-actions
        v-if="bcDocumentStore.document.approval && !isDropActive"
        :approval="bcDocumentStore.document.approval"
        :document="bcDocumentStore.document"
        :edit="bcDocumentStore.edit"
      />

      <document-upload-drop-zone
        class="Content__Document mx-auto !bg-transparent flex-row items-stretch !py-0"
        v-if="bcDocumentStore.edit && !isDropActive"
        :upload-enabled="true"
        :visible="true"
        v-model="bcDocumentStore.document"
        v-model:modelChildren="children"
        v-bc-get-box-model="{name: 'documentUploadDropZone'}"
      />

      <!--Search state -->
      <document-search
        class="mt-0 lg:-mt-8"
        v-if="bcDocumentStore.search && !isDropActive && childrenFiltered.length > 0 && !bcDocumentStore.edit"
        :found-results="foundSearchResults"
      />
      <aw-paddinger-bottom/>
    </template>
  </aw-drop>
  <!-- BcGallery wrapper for all galeries in document -->
  <router-view v-if="documentLoaded"/>
</template>

<script>
import { mapStores } from 'pinia'
import useBcDocumentStore from '@/frontapp/store/bcDocument'
import useBcFolderStore from '@/frontapp/store/bcFolder'
import useBcSelectedStore from '@/frontapp/store/bcSelected'
import uniqBy from 'lodash/uniqBy'
import DragDropElement from '../mixins/DragDropElement'
import { search } from 'ss-search'
import { trackPageView } from '../gtm'
import { showConfirm } from '../components/BcConfirm.vue'
import AsideModal from '../mixins/AsideModal'
import PublishDocument from '../mixins/actions/PublishDocument'
import loadingImageQueue from '../loadingImageQueue'

export default {
  mixins: [
    AsideModal,
    DragDropElement,
  ],
  props: ['id', 'rootId', 'revId', 'lang'],
  allowedChildElements: ['page'],
  data() {
    return {
      foundSearchResults: 0,
      searchResults: [],
      documentLoaded: false,
      editProducts: false,
    }
  },
  computed: {
    ...mapStores(useBcDocumentStore, useBcFolderStore, useBcSelectedStore),
    element () {
      return this.bcDocumentStore.document
    },
    searchQuery() {
      if (!this.bcDocumentStore.edit && this.bcDocumentStore.search) {
        return this.bcDocumentStore.search
      }

      return null
    },
    childrenFiltered () {
      if (this.searchQuery) {
        return this.searchResults
      
      } else {
        return this.children
      }
    },
    isEveryElementLocked() {
      let hasElements = false
      for (let e of this.bcSelectedStore.selectableMap.values()) {
        if (!e.locked) {
          return false
        }
        hasElements = true
      }

      return hasElements
    },
    isEveryElementTypeLocked() {
      let hasElements = false
      for (let e of this.bcSelectedStore.selectableMap.values()) {
        if (e.type !== 'locked') {
          return false
        }
        hasElements = true
      }

      return hasElements
    },
  },
  mounted() {
    console.log('mounted')
    this.openDocument(parseInt(this.rootId), parseInt(this.id), this.revId ? parseInt(this.revId) : undefined)
    this.$mitt.on('bc-delete-file', this.updateDeletedFiles)
  },
  beforeUnmount() {
    this.bcDocumentStore.edit = false
    this.$mitt.off('bc-delete-file', this.updateDeletedFiles)
    // close right menu
    // this.bcFolderStore.setLeftMenuOpened({}, {})
  },
  async beforeRouteEnter(to, from, next) {
    console.log('document beforeRouteEnter', { from, to })
    // before route update will not be called -> need to load document in route update -> happens when route changed from "BCDocumentDetail" to "BCDocumentDetailRev"
    if ((from.meta.netteRoute === "/<lang>/document" && to.meta.netteRoute === "/<lang>/document") || (from.meta.netteRoute === "/<lang>/shared-document" && to.meta.netteRoute === "/<lang>/shared-document")) {
      next((vm) => vm.openDocument(parseInt(to.params.rootId), parseInt(to.params.id), to.params.revId ? parseInt(to.params.revId) : undefined))
    }

    next()
  },
  async beforeRouteLeave() {
    return this.documentRouteWillChange()
  },
  async beforeRouteUpdate(to, from) {
    console.log('document beforeRouteUpdate', {from, to})
    if (from.meta.netteRoute === "/<lang>/document" && to.meta.netteRoute === "/<lang>/document"
        && (from.params.id !== to.params.id || from.params?.revId !== to.params?.revId)) {
      await this.documentRouteWillChange()
      return this.openDocument(parseInt(to.params.rootId), parseInt(to.params.id), to.params.revId ? parseInt(to.params.revId) : undefined)

    } else if (from.meta.netteRoute === "/<lang>/shared-document" && to.meta.netteRoute === "/<lang>/shared-document" && from.params.id !== to.params.id) {
      return this.openDocument(parseInt(to.params.rootId), parseInt(to.params.id), undefined)
    }
  },
  methods: {
    // on bc-delete-file event 
    // fetches new document revision data
    // replaces document files with updated files from new document revision response data
    async updateDeletedFiles(e) {
      const newDocumentDetailResponse = await this.bcDocumentStore.getDocumentDetailRevision(parseInt(this.id), this.revId ? parseInt(this.revId) : undefined)
      const newDocumentElements = new Map()
      this.bcDocumentStore.flattenDocumentRecursion(newDocumentDetailResponse.data.children, newDocumentElements, 'id') 

      newDocumentElements.forEach((newElement, i) => {
        if (!newElement.file) return

        if (e.fileIds.includes(newElement.file.id)) {
          this.bcDocumentStore.getFlattenDocument().get(i).file = newElement.file
        }
      })
    },
    toggleEditProducts(val) {
      this.editProducts = val
    },
    async openDocument(rootId, documentId, revId) {
      const shared = this.$route.matched[0].name === 'BCSharedDocument'
      const trash = this.$route.matched[0].name === 'BCTrashDocument'

      this.bcFolderStore.show = shared ? 'shared' : (trash ? 'trash' : 'bc')
      console.log('loading new revision of document in openDocument')
      await this.bcDocumentStore.loadDocument(documentId, revId, shared)

      if (this.bcDocumentStore.document.deleted && !trash) {
        return '/trash-document/' + rootId + '/' + documentId + (revId ? '/' + revId : '')
        //return await this.$router.replace('/trash-document/' + rootId + '/' + documentId + (revId ? '/' + revId : ''))
      } else if (trash && !this.bcDocumentStore.document.deleted) {
        return '/document/' + rootId + '/' + documentId + (revId ? '/' + revId : '')
        //return await this.$router.replace('/document/' + rootId + '/' + documentId + (revId ? '/' + revId : ''))
      }

      const loadFoldersPromises = []
      if (rootId)
        loadFoldersPromises.push(this.bcFolderStore?.loadFolder(
          null
        ))

      loadFoldersPromises.push(this.bcFolderStore?.loadFolder(
        rootId ? rootId : null
      ))

      await Promise.all(loadFoldersPromises)

      this.bcFolderStore?.enter(
        this.bcDocumentStore.document.bc_folder_id,
        documentId,
        trash ? this.bcDocumentStore.document.name : undefined,
        trash ? this.bcDocumentStore.document.url : undefined,
      )

      this.bcSelectedStore?.reset(
        () => this.bcDocumentStore.getFlattenDocument(),
        'id',
        revId
          ? 'parent__bc_document_data_rev_id'
          : 'parent__bc_document_data_id',
        this.bcDocumentStore.document
      )

      this.bcDocumentStore.loading = false

      if (!shared) {
        if (this.bcDocumentStore.document.can_edit) {
          this.bcDocumentStore?.loadDocumentRevisions(documentId)
        }
        
        if (this.bcDocumentStore && !trash) {
          this.bcDocumentStore.recentlyViewed = uniqBy([].concat([this.bcDocumentStore.document], this.bcDocumentStore.recentlyViewed), 'id')
        }
      }

      if (this.searchQuery) {
        this.foundSearchResults = 0
        this.searchResults = this.search(this.bcDocumentStore.document.children)
      }

      this.$nextTick(() => {
        if (this.$route.hash) {
          const htmlEl = document.getElementById(this.$route.hash.substring(1))
          if (htmlEl) {
            const offset = 150 // todo zjistovat
            const bodyRect = document.body.getBoundingClientRect().top
            const elementRect = htmlEl.getBoundingClientRect().top
            const offsetPosition = elementRect - bodyRect - offset

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            })
          }
        }

        trackPageView()
      })

      this.documentLoaded = true
      return true
    },
    search(children) {
      const ret = []
      let filteredChildrens
      for (let index = 0; index < children.length; index++) {
        const element = children[index];

        if (this.$route.hash && this.$route.hash.substring(3) == element.id) {
          this.foundSearchResults += 1
          filteredChildrens = []
          if (element.children && element.children.length) {
            filteredChildrens = this.search(element.children)
            if (filteredChildrens.length) {
              this.foundSearchResults += filteredChildrens.length
            }
          }

          ret.push({ ...element, children: filteredChildrens })
          continue
        }

        switch (element.type) {
          case 'files':
          case 'gallery':
          case 'image':
          case 'preview':
            filteredChildrens = search(element.children, ['data.name', 'file.name', 'file.ext', 'file.tags'], this.searchQuery)
            if (filteredChildrens.length) {
              this.foundSearchResults += filteredChildrens.length
              ret.push({ ...element, children: filteredChildrens })
            }
            break
        
          case 'text':
            if (search([element.data], ['html'], this.searchQuery).length) {
              this.foundSearchResults += 1
              ret.push(element)
            }
            break
          
          case 'header':
          case 'page':
            filteredChildrens = this.search(element.children)
            if (search([element.data], ['header'], this.searchQuery).length || filteredChildrens.length) {
              this.foundSearchResults += filteredChildrens.length === 0 ? 1 : 0
              ret.push({ ...element, children: filteredChildrens })
            }
            break
          
          case 'color':
            filteredChildrens = search(element.children, ['data.name', 'data.value'], this.searchQuery)
            if (search([element.data], ['name', 'r', 'g', 'b'], this.searchQuery).length || filteredChildrens.length) {
              this.foundSearchResults += filteredChildrens.length || 1
              ret.push({ ...element, children: filteredChildrens })
            }
            break
          
          case 'table':
            filteredChildrens = search(element.children, ['data.cols'], this.searchQuery)
            if (search([element.data], ['headers'], this.searchQuery).length || filteredChildrens.length) {
              this.foundSearchResults += filteredChildrens.length || 1
              ret.push({ ...element, children: filteredChildrens })
            }
            break 
        }
      }
      return ret
    },
    async documentRouteWillChange() {
      loadingImageQueue.stop()
      loadingImageQueue.clear()
      
      const isActivePublishDocumentBtn = this.bcDocumentStore.document?.published__bc_document_rev_id !== this.bcDocumentStore.documentRev
        || this.bcDocumentStore.document?.hidden;

      if (!isActivePublishDocumentBtn || !this.bcDocumentStore.edit) {
        if (!this.bcDocumentStore.willChangeRevision) {
          this.bcDocumentStore.reset()
        }
        return true
      }

      const confirm = await showConfirm({
        buttonNo: 'Leave',
        buttonNoClass: '!bg-bc-blue !text-white',
        buttonYes: 'Publish and leave',
        buttonYesClass: '!bg-green-600',
        header: this.$t('You are about to leave the document without publishing. Users may not see the changes you have made.'),
        withCloseIcon: true,
      })

      if (confirm === null) {
        return false
      }

      if (confirm) {
        await PublishDocument(this.bcDocumentStore, this.bcFolderStore, this.$router, this.$t, false)
      }

      this.bcDocumentStore.reset()
      return true
    }
  },
  watch: {
    searchQuery: {
      handler(val) {
        this.foundSearchResults = 0
        if (!val || !this.bcDocumentStore.document?.children) {
          this.searchResults = []
          return
        }

        this.searchResults = this.search(this.bcDocumentStore.document.children)
      },
      immediate: true,
    },
    isEveryElementLocked(newVal) {
      if (!newVal) {
        this.openDocument(parseInt(this.rootId), parseInt(this.id), this.revId ? parseInt(this.revId) : undefined)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.DocumentLocked__Wrapper {
  padding-top: 0;
}
</style>
